// Helpers de formulário compartilhados pelas telas.
import { ApiError, errorMessage } from "./api";
import { audioPrefs } from "./audio/sfx";
import { LANGS, lang, t } from "./i18n";

export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

/** Seletor de idioma (o main.ts escuta a troca e redesenha a tela). */
/** Botão de som (liga/desliga efeitos e vibração). */
export function soundButton(): string {
  const on = audioPrefs().sound;
  return `<button class="bw-btn bw-btn--square" data-sound-toggle aria-pressed="${on}" aria-label="${t("common.sound")}" title="${t("common.sound")}">${on ? "🔊" : "🔇"}</button>`;
}

export function langSelect(): string {
  return `<select class="bw-input lang-select" data-lang-select aria-label="${t("common.language")}">${LANGS.map((l) => `<option value="${l.id}" ${l.id === lang() ? "selected" : ""}>${l.label}</option>`).join("")}</select>`;
}

/** Tela com barra superior (voltar + título + idioma + tema) e corpo centralizado. */
export function screen(title: string, back: string | null, body: string): string {
  return `
    <div class="screen">
      <header class="screen__top">
        ${back ? `<a class="bw-btn bw-btn--square" href="#${back}" aria-label="${t("common.back")}">◀</a>` : "<span></span>"}
        <h1 class="bw-title screen__title">${title}</h1>
        <span class="top-tools">${langSelect()}${soundButton()}<button class="bw-btn bw-btn--square" data-theme-toggle aria-label="${t("common.theme")}">B/W</button></span>
      </header>
      <main class="screen__body">${body}</main>
    </div>`;
}

export function field(name: string, label: string, opts: { type?: string; autocomplete?: string; hint?: string; value?: string } = {}): string {
  return `
    <div class="bw-field">
      <label for="f-${name}">${label}</label>
      <input class="bw-input" id="f-${name}" name="${name}" type="${opts.type ?? "text"}"
        ${opts.autocomplete ? `autocomplete="${opts.autocomplete}"` : ""} ${opts.value ? `value="${esc(opts.value)}"` : ""}
        aria-describedby="h-${name}" autocapitalize="off" spellcheck="false">
      <span class="bw-hint" id="h-${name}" data-default="${esc(opts.hint ?? "")}">${opts.hint ?? ""}</span>
    </div>`;
}

/** Liga um formulário: coleta campos, mostra erros por campo e de topo, trava o botão. */
export function bindForm<T extends Record<string, string>>(
  form: HTMLFormElement,
  submit: (values: T) => Promise<void>,
  validate?: (values: T) => Record<string, string>,
) {
  const alert = form.querySelector<HTMLElement>("[data-alert]");
  const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');

  const clear = () => {
    form.querySelectorAll<HTMLInputElement>(".bw-input").forEach((i) => i.removeAttribute("aria-invalid"));
    form.querySelectorAll<HTMLElement>(".bw-hint").forEach((h) => {
      h.textContent = h.dataset.default ?? "";
      h.classList.remove("bw-hint--error");
    });
    if (alert) alert.hidden = true;
  };
  const showFields = (fields: Record<string, string>) => {
    for (const [name, msg] of Object.entries(fields)) {
      const input = form.querySelector<HTMLInputElement>(`[name="${name}"]`);
      const hint = form.querySelector<HTMLElement>(`#h-${name}`);
      input?.setAttribute("aria-invalid", "true");
      if (hint) {
        hint.textContent = msg;
        hint.classList.add("bw-hint--error");
      }
    }
    form.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
  };
  const showAlert = (msg: string, kind: "error" | "ok" | "info" = "error") => {
    if (!alert) return;
    alert.textContent = msg;
    alert.className = `bw-alert bw-alert--${kind}`;
    alert.hidden = false;
  };

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    clear();
    const values = Object.fromEntries(new FormData(form).entries()) as T;
    const local = validate?.(values) ?? {};
    if (Object.keys(local).length) return showFields(local);
    button?.setAttribute("aria-busy", "true");
    try {
      await submit(values);
    } catch (e) {
      if (e instanceof ApiError && Object.keys(e.fields).length) showFields(e.fields);
      showAlert(errorMessage(e));
    } finally {
      button?.removeAttribute("aria-busy");
    }
  });
  return { showAlert };
}
